<script setup>
import BaseCard from "@/components/BaseCard.vue";
import AttemptSelectorTypeElement from "@/components/attempt-selector/AttemptSelectorTypeElement.vue";

import {
  ATTEMPT_BY_GENERAL_PROCESAL,
  ATTEMPT_CUSTOM,
  ATTEMPT_CUSTOM_BLANK,
  ATTEMPT_CUSTOM_FAVOURITES,
  ATTEMPT_CUSTOM_INCORRECT,
  ATTEMPT_CUSTOM_UNCERTAIN,
  ATTEMPT_PRACTICAL_CASE,
  ATTEMPT_PRACTICAL_CASE_CC,
  ATTEMPT_PRACTICAL_CASE_PREVIOUS_CALL,
  ATTEMPT_PRACTICAL_CASE_PREVIOUS_CALL_CC,
  ATTEMPT_PREVIOUS_CALL,
  ATTEMPT_PREVIOUS_CALL_CC,
  ATTEMPT_TYPE_TITLES,
} from "@/components/utils.js";
import { computed } from "vue";
import { attemptTypeIconSelector } from "@/components/icons.js";
import AttemptSelectorHeader from "@/components/attempt-selector/AttemptSelectorHeader.vue";

const props = defineProps({
  publicExamination: {
    type: Object,
    required: true,
  },
  examStatistics: {
    type: [Object, null],
    required: true,
  },
});

const attemptTypes = computed(() => {
  const attempts = [
    {
      id: "bolsa",
      title: "Configura tu test por bolsa de preguntas y exámenes oficiales",
      color: "green",
      elements: [
        {
          type: ATTEMPT_CUSTOM,
          description: "Crea test personalizados, elige el número de preguntas y selecciona el contenido",
        },
      ],
    },
    {
      id: "historico",
      title: "De tu histórico de test realizados",
      color: "orange",
      elements: [
        {
          type: ATTEMPT_CUSTOM_INCORRECT,
          description: "Aquellas falladas",
        },
        {
          type: ATTEMPT_CUSTOM_BLANK,
          description: "Aquellas en blanco",
        },
        {
          type: ATTEMPT_CUSTOM_FAVOURITES,
          description: "Aquellas marcadas como importante",
        },
        {
          type: ATTEMPT_CUSTOM_UNCERTAIN,
          description: "Aquellas dudosas",
        },
      ],
    },
  ];

  if (props.examStatistics != null) {
    if (props.examStatistics[ATTEMPT_PREVIOUS_CALL_CC] > 0) {
      attempts[0].elements.push({
        type: ATTEMPT_PREVIOUS_CALL,
        description: "Test reales de convocatorias anteriores, siempre actualizados",
      });
    }
    if (
      props.examStatistics[ATTEMPT_PRACTICAL_CASE_CC] > 0 ||
      props.examStatistics[ATTEMPT_PRACTICAL_CASE_PREVIOUS_CALL_CC] > 0
    ) {
      const exams = {
        id: "supuestos",
        title: "Especial supuestos prácticos",
        color: "red",
        elements: [],
      };
      if (props.examStatistics[ATTEMPT_PRACTICAL_CASE_CC] > 0) {
        exams.elements.push({
          type: ATTEMPT_PRACTICAL_CASE,
          description: "Prueba nuestros supuestos prácticos ",
        });
      }
      if (props.examStatistics[ATTEMPT_PRACTICAL_CASE_PREVIOUS_CALL_CC] > 0) {
        exams.elements.push({
          type: ATTEMPT_PRACTICAL_CASE_PREVIOUS_CALL,
          description: "Supuestos prácticos de exámenes reales de convocatorias anteriores",
        });
      }

      attempts.push(exams);
    }
  }

  if (props.publicExamination.shortName !== "Ofimática Tramitación Procesal") {
    // hack at the speed of too fast to furious because i don't have more time, if the opposition name change
    // this will fail
    attempts[0].elements.push({
      type: ATTEMPT_BY_GENERAL_PROCESAL,
      description: "Crea test con preguntas de repaso, el número de preguntas y selecciona el contenido",
    });
  }

  return attempts;
});

const emit = defineEmits(["unselect-public-examination", "select-type"]);

function unselectPublicExamination() {
  emit("unselect-public-examination");
}

function selectType(type) {
  emit("select-type", type);
}

function sectionTitle(type) {
  let result = ATTEMPT_TYPE_TITLES.filter((obj) => {
    return obj.value === type;
  });
  return result[0].title;
}

function selectIcon(type) {
  return attemptTypeIconSelector(type);
}

const oppositionsData = {
  "Auxilio Judicial": {
    custom: "13k",
    by_general_procesal: "6.5k",
    previous_call: "2k",
    previous_call_exam: "22 tests",
    practical_case: "900",
    practical_case_exam: "40 supuestos",
    practical_case_previous_call: "700",
    practical_case_previous_call_exam: "30 supuestos oficiales",
  },
  "Tramitación Procesal TL": {
    custom: "14k",
    by_general_procesal: "6.5k",
    previous_call: "2.7k",
    previous_call_exam: "31 tests",
    practical_case: "900",
    practical_case_exam: "45 supuestos",
    practical_case_previous_call: "250",
    practical_case_previous_call_exam: "13 supuestos oficiales",
  },
  "Gestión Procesal TL": {
    custom: "17k",
    by_general_procesal: "9.5k",
    previous_call: "2.6k",
    previous_call_exam: "30 tests",
    practical_case: "1k",
    practical_case_exam: "50 supuestos",
    practical_case_previous_call: "160",
    practical_case_previous_call_exam: "9 supuestos oficiales",
  },
  "Tramitación Procesal PI": {
    custom: "12k",
    by_general_procesal: "5.7k",
    previous_call: "650",
    previous_call_exam: "9 tests",
    practical_case: "",
    practical_case_exam: "",
    practical_case_previous_call: "50",
    practical_case_previous_call_exam: "5 supuestos oficiales",
  },
  "Gestión Procesal PI": {
    custom: "10k",
    by_general_procesal: "7.3k",
    previous_call: "700",
    previous_call_exam: "10 tests",
    practical_case: "",
    practical_case_exam: "",
    practical_case_previous_call: "88",
    practical_case_previous_call_exam: "5 supuestos oficiales",
  },
  "Ofimática Tramitación Procesal": {
    custom: "800",
    by_general_procesal: "",
    previous_call: "",
    previous_call_exam: "",
    practical_case: "",
    practical_case_exam: "",
    practical_case_previous_call: "",
    practical_case_previous_call_exam: "",
  },
};
</script>

<template>
  <div>
    <attempt-selector-header
      :public-examination="publicExamination"
      :selected-exam="null"
      :selected-type="null"
      :selected-difficulty="null"
      :questions="null"
      :selected-contents="null"
      @unselect-public-examination="unselectPublicExamination"
    />

    <base-card v-for="(card, index) in attemptTypes" :key="index" class="mt-4">
      <div class="opp-conf-grid">
        <h3 class="mb-7 ml-5 mt-3 text-xl">{{ card.title }}</h3>
        <attempt-selector-type-element
          v-for="(element, elementIndex) in card.elements"
          :key="elementIndex"
          :type="element.type"
          :circle-color="card.color"
          :description="element.description"
          :number-questions="oppositionsData[publicExamination.shortName][element.type] ?? ''"
          :number-exams="oppositionsData[publicExamination.shortName][element.type + '_exam'] ?? ''"
          :pill-text="
            card.id === 'bolsa'
              ? 'Preguntas y exámenes'
              : card.id === 'historico'
              ? 'Histórico de test'
              : card.id === 'supuestos'
              ? 'Supuestos prácticos'
              : ''
          "
          :class="[
            'opp-grid-item',
            card.id === 'bolsa' ? 'preguntas' : '',
            card.id === 'historico' ? 'historico' : '',
            card.id === 'supuestos' ? 'supuestos' : '',
          ]"
          @select-type="selectType"
        >
          {{ sectionTitle(element.type) }}
          <template v-slot:icon>
            <component v-bind:is="selectIcon(element.type)" class="w-full" />
          </template>
        </attempt-selector-type-element>
      </div>
    </base-card>
  </div>
</template>
